import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import InstallmentTimeline from '../components/InstallmentTimeline'
import '../theme/InstallmentPromotion.css'

const InstallmentPromotion: React.FC = () => {
  const { search } = useLocation()
  const queryParams = new URLSearchParams(search)
  const locale = queryParams.get('locale')
  const installmentsCount = Number(queryParams.get('installmentsCount') ?? 12)
  const price = Number(queryParams.get('price') ?? 5000)
  const { t, i18n } = useTranslation()
  const [selectedMonths, setSelectedMonths] = useState(installmentsCount)
  const [showMonthsDropdown, setShowMonthsDropdown] = useState(false)
  const generateMonthOptions = (): number[] => {
    const standardOptions = [3, 6, 9, 12]
    if (standardOptions.includes(installmentsCount)) {
      return standardOptions
    }
    const newOptions = [...standardOptions]
    if (!newOptions.includes(installmentsCount)) {
      newOptions.push(installmentsCount)
      newOptions.sort((a, b) => a - b)
    }
    return newOptions
  }

  const monthOptions = generateMonthOptions()

  const handleMonthSelect = (option: number): void => {
    setSelectedMonths(option)
    setShowMonthsDropdown(false)
  }

  const bankLogos = {
    en: [
      { name: 'Al Rajhi Bank', logo: '/assets/images/banks/0.png' },
      { name: 'SNB', logo: '/assets/images/banks/1.png' },
      { name: 'Al Inma Bank', logo: '/assets/images/banks/2.png' },
      { name: 'SAB', logo: '/assets/images/banks/3.png' }
    ],
    ar: [
      { name: 'مصرف الراجحي', logo: '/assets/images/banks/0_ar.png' },
      { name: 'البنك الأهلي السعودي', logo: '/assets/images/banks/1_ar.png' },
      { name: 'مصرف الإنماء', logo: '/assets/images/banks/2_ar.png' },
      { name: 'البنك السعودي العربي', logo: '/assets/images/banks/3_ar.png' }
    ]
  }
  const currentLocale = i18n.language.startsWith('ar') ? 'ar' : 'en'
  const banks = bankLogos[currentLocale]

  useEffect(() => {
    if (locale === 'ar') {
      // Fix 1: Add braces to the arrow function
      i18n.changeLanguage('ar').catch((err) => {
        console.error(err)
      })
      document.body.setAttribute('dir', 'rtl')
    }
  }, [locale, i18n])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      if (showMonthsDropdown && !target.closest('.month-selector')) {
        setShowMonthsDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMonthsDropdown])

  // Calculate installment amount
  const installmentAmount = (price / selectedMonths).toFixed(1)

  return (
    <div className="amwal-installment-container">
      {/* Main Title */}
      <div className="title-container">
        <h1>
          {t('Convert your purchases to up to')} <br />
          <span className="title-highlight">{12} {t('Months')}</span> { locale === 'en' ? t('installments') : t('')}
        </h1>
      </div>

      {/* Bank Logos */}
      <div className="banks-container">
        {banks.map((bank, index) => (
          <img
            key={index}
            src={bank.logo}
            alt={bank.name}
            className="bank-logo"
          />
        ))}
      </div>

      {/* Flexible Plans Section */}
      <div className="plans-section">
        <div className="plans-title">{t('Flexible Plans, Your Choice')}</div>
        <div className="month-selector" onClick={() => {
          setShowMonthsDropdown(!showMonthsDropdown)
        }}>
          {selectedMonths} {t('months')} <span className="dropdown-icon"></span>

          {/* Dropdown for month selection */}
          {showMonthsDropdown && (
            <div className="months-dropdown">
              {monthOptions.map((option) => (
                <div
                  key={option}
                  className={`dropdown-item ${selectedMonths === option ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleMonthSelect(option)
                  }}
                >
                  {option} {t('months')}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Installment Timeline */}
      <InstallmentTimeline
        price={price}
        installmentAmount={installmentAmount}
        selectedMonths={selectedMonths}
        t={t}
        locale={currentLocale}
      />

      {/* How It Works Section */}
      <div className="how-it-works">
        <h2 className={'benefits-title'}>{t('How it works')}</h2>
        <ul className="steps-list">
          <li className="step-item">
            <div className="step-number">1</div>
            <div className="step-text">{t('Select Amwal at checkout')}</div>
          </li>
          <li className="step-item">
            <div className="step-number">2</div>
            <div className="step-text">{t('Choose your bank and installment plan (3, 6, 12 months)')}</div>
          </li>
          <li className="step-item">
            <div className="step-number">3</div>
            <div className="step-text">{t('Insert your selected bank credit card')}</div>
          </li>
          <li className="step-item">
            <div className="step-number">4</div>
            <div className="step-text">{t('The full amount will be held before installments starting.')}</div>
          </li>
        </ul>
      </div>

      {/* Why Amwal Section */}
      <div className="benefits-section">
        <div className="benefits-title">{t('Why amwal?')}</div>
        <div className="benefits-container">
          <div className="benefit-card">
            <div className="benefit-icon">0%</div>
            <div className="benefit-label">{t('No fees')}</div>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon">
              <img src="/assets/images/sharia.svg" alt="Sharia-compliant" width={43} />
            </div>
            <div className="benefit-label">{t('Sharia-compliant')}</div>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon">
              <img src="/assets/images/formkit_happy.svg" alt="Hassle-free" width={43} />
            </div>
            <div className="benefit-label">{t('Hassle-free')}</div>
          </div>
        </div>
      </div>

      {/* Disclaimer Section */}
      <div className="disclaimer">
        <p style={{ fontSize: '18px', lineHeight: '1.5' }}>
          {t('Payment plan availability may vary based on your order value and your amwal profile. All plans are subject to')} <a href="https://www.amwal.tech/legal/terms-of-service" className="terms-link">{t('terms and conditions')}</a>. {t('amwal is fully Shariah-compliant and available to customers in Saudi Arabia. Your final')} <a href="#" className="terms-link">{t('payment terms')}</a> {t('may vary depending on your credit card issuer and approval status.')}
        </p>

        <div className="card-logos">
          <div className="security-badge">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L4 5V11.09C4 16.14 7.41 20.85 12 22C16.59 20.85 20 16.14 20 11.09V5L12 2ZM12 20C8.73 19 6 15.1 6 11.09V6.31L12 4.19L18 6.31V11.09C18 15.1 15.27 19 12 20Z" fill="#26a96c"/>
              <path d="M9 11.5L11 13.5L15.5 9" stroke="#26a96c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('Secure Transaction')}</span>
          </div>
          <div className="payment-cards">
            <img src="/assets/images/payment-cards.svg" alt="Payment Cards" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default InstallmentPromotion
